import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import PageShell from "../dashboard/PageShell";
import {
  Plus,
  Search,
  FileDown,
  ChevronDown,
  MoreHorizontal,
  Eye,
  Pencil,
  Trash2,
} from "lucide-react";

interface ScientificArticleItem {
  id: string;
  stt: number;
  articleName: string;
  leadUnit: string;
  authorCount: number;
  mainAuthor: string;
  coAuthorCenter: string;
  coAuthorOutside: string;
  journal: string;
  doi: string;
  publicationYear: string;
  impactFactor: string;
  journalCategory: string;
  taskProduct: string;
  awards: string;
  articleFile: string;
}

const mockData: ScientificArticleItem[] = [
  {
    id: "1",
    stt: 1,
    articleName: "Tạp chí 2",
    leadUnit: "Viện Thủy công",
    authorCount: 0,
    mainAuthor: "GS. ThS Lê Anh Tuấn",
    coAuthorCenter: "",
    coAuthorOutside: "",
    journal: "",
    doi: "",
    publicationYear: "3/2020",
    impactFactor: "",
    journalCategory: "",
    taskProduct: "",
    awards: "",
    articleFile: "",
  },
  {
    id: "2",
    stt: 2,
    articleName: "Tạp chí 1",
    leadUnit: "Viện Nước, Tưới Tiêu và Môi trường",
    authorCount: 0,
    mainAuthor: "GS. ThS Lê Anh Tuấn",
    coAuthorCenter: "",
    coAuthorOutside: "",
    journal: "",
    doi: "",
    publicationYear: "4/2022",
    impactFactor: "",
    journalCategory: "",
    taskProduct: "",
    awards: "",
    articleFile: "",
  },
  {
    id: "3",
    stt: 3,
    articleName: "Bài báo",
    leadUnit: "Ban Tổng Giám đốc",
    authorCount: 0,
    mainAuthor: "Thượng tá, VS. ThS Lê Anh Tuấn",
    coAuthorCenter: "",
    coAuthorOutside: "",
    journal: "",
    doi: "",
    publicationYear: "5/2021",
    impactFactor: "",
    journalCategory: "Proceedings of the conference",
    taskProduct: "",
    awards: "",
    articleFile: "",
  },
  {
    id: "4",
    stt: 4,
    articleName: "Bài báo 123",
    leadUnit: "Viện Nước, Tưới Tiêu và Môi trường",
    authorCount: 22,
    mainAuthor: "ThS Lê Anh Tuấn",
    coAuthorCenter: "Sơn",
    coAuthorOutside: "",
    journal: "Tạp chí",
    doi: "",
    publicationYear: "7/2021",
    impactFactor: "",
    journalCategory: "",
    taskProduct:
      "Thực trạng tổ chức hoạt động âm nhạc ở một số trường mầm non tại Thành phố Huế",
    awards: "60.000.000 đ",
    articleFile: "",
  },
  {
    id: "5",
    stt: 5,
    articleName:
      "RESEARCHING SOME METHODS TO ENHANCE LEARNERS' SPEAKING SKILLS IN ESL CLASS",
    leadUnit: "Viện Nước, Tưới Tiêu và Môi trường",
    authorCount: 6,
    mainAuthor: "Thượng tá, VS. ThS Lê Anh Tuấn",
    coAuthorCenter: "GS. ThS Lê Anh Tuấn; Nguyễn Thị Hạnh",
    coAuthorOutside: "",
    journal:
      "The 32nd Annual Conference of the Asian Association of Open UniversitiesOPEN EDUCATION IN HUMAN RESOURCE DEVELOPMENT IN ASIA'S PERIOD OF INTEGRATION",
    doi: "DOI",
    publicationYear: "3/2018",
    impactFactor: "IF",
    journalCategory: "SCIE",
    taskProduct: "",
    awards: "",
    articleFile: "",
  },
  {
    id: "6",
    stt: 6,
    articleName: "Analysing the structure of vietnamese female names",
    leadUnit: "Phòng Nghiên cứu khoa học và Hợp tác",
    authorCount: 0,
    mainAuthor: "Lê Thị Minh Thảo",
    coAuthorCenter: "",
    coAuthorOutside: "",
    journal:
      "The international conference on linguistics in vietnam - 2018 (ICLV)",
    doi: "",
    publicationYear: "0",
    impactFactor: "",
    journalCategory: "",
    taskProduct: "",
    awards: "",
    articleFile: "",
  },
];

const ScientificArticles: React.FC = () => {
  const navigate = useNavigate();
  const [selectedItems, setSelectedItems] = useState<number[]>([]);
  const [openMenuArticleId, setOpenMenuArticleId] = useState<string | null>(
    null
  );
  const [menuPlacement, setMenuPlacement] = useState<"bottom" | "top">(
    "bottom"
  );
  const [buttonPosition, setButtonPosition] = useState<{
    left: number;
    top: number;
    right: number;
    bottom: number;
  } | null>(null);
  const dropdownRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});

  // Delete modal state
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteArticleId, setDeleteArticleId] = useState<string | null>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (openMenuArticleId) {
        const currentRef = dropdownRefs.current[openMenuArticleId];
        if (currentRef && !currentRef.contains(event.target as Node)) {
          setOpenMenuArticleId(null);
        }
      }
    };

    const handleScroll = () => {
      if (openMenuArticleId && buttonPosition) {
        // Update position on scroll to keep menu attached to button
        const button = document.querySelector(
          `[data-article-id="${openMenuArticleId}"]`
        ) as HTMLButtonElement;
        if (button) {
          const rect = button.getBoundingClientRect();
          setButtonPosition({
            left: rect.left,
            top: rect.top,
            right: rect.right,
            bottom: rect.bottom,
          });
        }
      }
    };

    if (openMenuArticleId) {
      document.addEventListener("mousedown", handleClickOutside);
      window.addEventListener("scroll", handleScroll, true);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      window.removeEventListener("scroll", handleScroll, true);
    };
  }, [openMenuArticleId, buttonPosition]);

  const handleDeleteConfirm = () => {
    if (deleteArticleId) {
      console.log("Deleting article:", deleteArticleId);
      // Here you would implement the actual delete logic
      setShowDeleteConfirm(false);
      setDeleteArticleId(null);
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteConfirm(false);
    setDeleteArticleId(null);
  };

  return (
    <PageShell title="Bài báo - Công trình khoa học">
      <div className="space-y-6">
        {/* Header Section */}
        <div className="flex justify-between items-center">
          <h1 className="text-xl font-semibold text-gray-800">
            Bài báo - Công trình khoa học
          </h1>
          <button
            onClick={() => navigate("/add-new-article")}
            className="bg-green-600 text-white px-3 py-2 rounded text-sm font-medium hover:bg-green-700 transition-colors flex items-center space-x-2"
          >
            <Plus className="h-4 w-4" />
            <span>Thêm mới Bài báo</span>
          </button>
        </div>

        {/* Search and Filter Section */}
        <div className="bg-white rounded-lg p-4">
          <div className="space-y-4">
            {/* Row 1 - Search input + Search button */}
            <div className="flex items-center space-x-4">
              <div className="relative w-96">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                <input
                  type="text"
                  placeholder="Tìm kiếm bài báo"
                  className="w-full pl-10 pr-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <button className="bg-green-600 text-white px-3 py-2 rounded text-sm font-medium hover:bg-green-700 transition-colors">
                Tìm kiếm
              </button>
            </div>

            {/* Row 2 - Filters */}
            <div className="grid grid-cols-6 gap-4">
              <select className="border border-gray-300 rounded-md px-2 py-2 text-sm bg-white">
                <option>- Đơn vị/ Phòng ban -</option>
              </select>
              <select className="border border-gray-300 rounded-md px-2 py-2 text-sm bg-white">
                <option>- Loại -</option>
              </select>
              <select className="border border-gray-300 rounded-md px-2 py-2 text-sm bg-white">
                <option>- Danh mục -</option>
              </select>
              <select className="border border-gray-300 rounded-md px-2 py-2 text-sm bg-white">
                <option>- Tác giả chính -</option>
              </select>
              <select className="border border-gray-300 rounded-md px-2 py-2 text-sm bg-white">
                <option>- Trực thuộc -</option>
              </select>
              <select className="border border-gray-300 rounded-md px-2 py-2 text-sm bg-white">
                <option>- Lĩnh vực nghiên cứu -</option>
              </select>
            </div>

            {/* Row 3 - Additional Filters */}
            <div className="grid grid-cols-6 gap-4">
              <select className="border border-gray-300 rounded-md px-2 py-2 text-sm bg-white">
                <option>- Từ năm -</option>
              </select>
              <select className="border border-gray-300 rounded-md px-2 py-2 text-sm bg-white">
                <option>- Đến năm -</option>
              </select>
            </div>

            {/* Row 4 - Export button */}
            <div className="flex justify-start">
              <button className="bg-orange-500 text-white px-3 py-2 rounded text-sm font-medium hover:bg-orange-600 transition-colors flex items-center space-x-2">
                <FileDown className="h-4 w-4" />
                <span>Xuất ra file</span>
              </button>
            </div>
          </div>
        </div>

        {/* Data Table */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                {/* First row - Main headers */}
                <tr>
                  <th className="px-2 py-3 text-left text-xs font-bold text-black uppercase tracking-wider border-r border-gray-200 w-6">
                    STT
                  </th>
                  <th className="px-2 py-3 text-left text-xs font-bold text-black uppercase tracking-wider border-r border-gray-200 w-40">
                    Tên bài báo công trình
                  </th>
                  <th className="px-2 py-3 text-left text-xs font-bold text-black uppercase tracking-wider border-r border-gray-200 w-28">
                    Đơn vị chủ trì
                  </th>
                  <th className="px-2 py-3 text-left text-xs font-bold text-black uppercase tracking-wider border-r border-gray-200 w-14">
                    Số tác giả
                  </th>
                  <th className="px-2 py-3 text-left text-xs font-bold text-black uppercase tracking-wider border-r border-gray-200 w-20">
                    Tác giả chính
                  </th>
                  <th
                    className="px-2 py-3 text-center text-xs font-bold text-black uppercase tracking-wider border-r border-gray-200 w-24"
                    colSpan={2}
                  >
                    Đồng tác giả
                  </th>
                  <th className="px-2 py-3 text-left text-xs font-bold text-black uppercase tracking-wider border-r border-gray-200 w-24">
                    Tạp chí
                  </th>
                  <th className="px-2 py-3 text-left text-xs font-bold text-black uppercase tracking-wider border-r border-gray-200 w-16">
                    DOI
                  </th>
                  <th className="px-2 py-3 text-left text-xs font-bold text-black uppercase tracking-wider border-r border-gray-200 w-16">
                    Năm công bố
                  </th>
                  <th
                    className="px-2 py-3 text-center text-xs font-bold text-black uppercase tracking-wider border-r border-gray-200 w-24"
                    colSpan={2}
                  >
                    Danh mục tạp chí
                  </th>
                  <th className="px-2 py-3 text-left text-xs font-bold text-black uppercase tracking-wider border-r border-gray-200 w-28">
                    Sp của nhiệm vụ
                  </th>
                  <th className="px-2 py-3 text-left text-xs font-bold text-black uppercase tracking-wider border-r border-gray-200 w-20">
                    Khen thưởng
                  </th>
                  <th className="px-2 py-3 text-left text-xs font-bold text-black uppercase tracking-wider border-r border-gray-200 w-20">
                    File bài báo
                  </th>
                  <th className="px-2 py-3 text-left text-xs font-bold text-black uppercase tracking-wider w-16">
                    Chức năng
                  </th>
                </tr>
                {/* Second row - Sub headers */}
                <tr>
                  <th className="px-2 py-2 text-left text-xs font-bold text-black uppercase tracking-wider border-r border-gray-200 w-6"></th>
                  <th className="px-2 py-2 text-left text-xs font-bold text-black uppercase tracking-wider border-r border-gray-200 w-40"></th>
                  <th className="px-2 py-2 text-left text-xs font-bold text-black uppercase tracking-wider border-r border-gray-200 w-28"></th>
                  <th className="px-2 py-2 text-left text-xs font-bold text-black uppercase tracking-wider border-r border-gray-200 w-14"></th>
                  <th className="px-2 py-2 text-left text-xs font-bold text-black uppercase tracking-wider border-r border-gray-200 w-20"></th>
                  <th className="px-2 py-2 text-left text-xs font-bold text-black uppercase tracking-wider border-r border-gray-200 w-12">
                    Thuộc trung tâm
                  </th>
                  <th className="px-2 py-2 text-left text-xs font-bold text-black uppercase tracking-wider border-r border-gray-200 w-12">
                    Ngoài trung tâm
                  </th>
                  <th className="px-2 py-2 text-left text-xs font-bold text-black uppercase tracking-wider border-r border-gray-200 w-24"></th>
                  <th className="px-2 py-2 text-left text-xs font-bold text-black uppercase tracking-wider border-r border-gray-200 w-16"></th>
                  <th className="px-2 py-2 text-left text-xs font-bold text-black uppercase tracking-wider border-r border-gray-200 w-16"></th>
                  <th className="px-2 py-2 text-left text-xs font-bold text-black uppercase tracking-wider border-r border-gray-200 w-12">
                    IF
                  </th>
                  <th className="px-2 py-2 text-left text-xs font-bold text-black uppercase tracking-wider border-r border-gray-200 w-12">
                    Danh mục
                  </th>
                  <th className="px-2 py-2 text-left text-xs font-bold text-black uppercase tracking-wider border-r border-gray-200 w-28"></th>
                  <th className="px-2 py-2 text-left text-xs font-bold text-black uppercase tracking-wider border-r border-gray-200 w-20"></th>
                  <th className="px-2 py-2 text-left text-xs font-bold text-black uppercase tracking-wider border-r border-gray-200 w-20"></th>
                  <th className="px-2 py-2 text-left text-xs font-bold text-black uppercase tracking-wider w-16"></th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {mockData.map((item) => (
                  <tr key={item.id} className="hover:bg-gray-50">
                    <td className="px-2 py-4 whitespace-nowrap text-sm font-medium text-gray-900 border-r border-gray-200 w-6">
                      {item.stt}
                    </td>
                    <td className="px-2 py-4 text-sm text-gray-900 border-r border-gray-200 w-40">
                      <div className="font-medium text-[#337ab7] text-[14px] leading-tight">
                        {item.articleName}
                      </div>
                    </td>
                    <td className="px-2 py-4 text-sm text-gray-900 border-r border-gray-200 w-28">
                      {item.leadUnit}
                    </td>
                    <td className="px-2 py-4 text-sm text-gray-900 border-r border-gray-200 w-14">
                      {item.authorCount > 0 ? item.authorCount : ""}
                    </td>
                    <td className="px-2 py-4 text-sm text-gray-900 border-r border-gray-200 w-20">
                      {item.mainAuthor}
                    </td>
                    <td className="px-2 py-4 text-sm text-gray-900 border-r border-gray-200 w-12">
                      {item.coAuthorCenter || ""}
                    </td>
                    <td className="px-2 py-4 text-sm text-gray-900 border-r border-gray-200 w-12">
                      {item.coAuthorOutside || ""}
                    </td>
                    <td className="px-2 py-4 text-sm text-gray-900 border-r border-gray-200 w-24">
                      {item.journal || ""}
                    </td>
                    <td className="px-2 py-4 text-sm text-gray-900 border-r border-gray-200 w-16">
                      {item.doi || ""}
                    </td>
                    <td className="px-2 py-4 text-sm text-gray-900 border-r border-gray-200 w-16">
                      {item.publicationYear}
                    </td>
                    <td className="px-2 py-4 text-sm text-gray-900 border-r border-gray-200 w-12">
                      {item.impactFactor || ""}
                    </td>
                    <td className="px-2 py-4 text-sm text-gray-900 border-r border-gray-200 w-12">
                      {item.journalCategory || ""}
                    </td>
                    <td className="px-2 py-4 text-sm text-gray-900 border-r border-gray-200 w-28">
                      {item.taskProduct || ""}
                    </td>
                    <td className="px-2 py-4 text-sm text-gray-900 border-r border-gray-200 w-20">
                      {item.awards || ""}
                    </td>
                    <td className="px-2 py-4 text-sm text-gray-900 border-r border-gray-200 w-20">
                      <button className="text-blue-600 hover:text-blue-800 font-medium">
                        {item.articleFile || ""}
                      </button>
                    </td>
                    <td className="px-2 py-4 whitespace-nowrap border-r border-gray-200 w-16">
                      <div
                        className="relative inline-block"
                        ref={(el) => {
                          dropdownRefs.current[item.id] = el;
                        }}
                      >
                        <button
                          data-article-id={item.id}
                          className="bg-[#337ab7] text-white px-2 py-1 rounded text-xs font-medium hover:bg-[#2e6da4] transition-colors flex items-center space-x-1"
                          onClick={(e) => {
                            const target = e.currentTarget as HTMLButtonElement;
                            const rect = target.getBoundingClientRect();
                            const estimatedMenuHeight = 200; // approx height for 3 items with padding
                            const spaceBelow = window.innerHeight - rect.bottom;
                            // More aggressive flipping - flip if less than 250px space below
                            const willOverflowBottom = spaceBelow < 250;
                            setMenuPlacement(
                              willOverflowBottom ? "top" : "bottom"
                            );
                            setButtonPosition({
                              left: rect.left,
                              top: rect.top,
                              right: rect.right,
                              bottom: rect.bottom,
                            });
                            setOpenMenuArticleId(
                              openMenuArticleId === item.id ? null : item.id
                            );
                          }}
                        >
                          <span>Chọn</span>
                          <ChevronDown className="h-3 w-3" />
                        </button>
                        {openMenuArticleId === item.id &&
                          createPortal(
                            <div
                              className={
                                `fixed w-48 bg-white rounded-md shadow-lg border border-gray-200 z-[99999] ` +
                                (menuPlacement === "bottom"
                                  ? "mt-2"
                                  : "bottom-full mb-2")
                              }
                              onMouseDown={(e) => {
                                // Prevent the global mousedown outside handler from closing
                                // the menu before the anchor click can navigate
                                e.stopPropagation();
                              }}
                              style={
                                buttonPosition
                                  ? {
                                      left: `${buttonPosition.right - 200}px`, // 200px = w-48 + 16px offset
                                      top:
                                        menuPlacement === "bottom"
                                          ? `${buttonPosition.bottom + 0}px`
                                          : `${buttonPosition.top - 180}px`,
                                      backgroundColor: "white",
                                      zIndex: 99999,
                                    }
                                  : {}
                              }
                            >
                              <div className="py-1 bg-white">
                                {[
                                  {
                                    label: "Chi tiết",
                                    Icon: Eye,
                                    colorClass: "text-blue-600",
                                  },
                                  {
                                    label: "Sửa",
                                    Icon: Pencil,
                                    colorClass: "text-amber-600",
                                  },
                                  {
                                    label: "Xóa",
                                    Icon: Trash2,
                                    colorClass: "text-red-600",
                                  },
                                ].map(({ label, Icon, colorClass }) =>
                                  label === "Chi tiết" ? (
                                    <button
                                      key={label}
                                      onClick={() => {
                                        setOpenMenuArticleId(null);
                                        navigate(`/articles/detail/${item.id}`);
                                      }}
                                      className="flex items-center gap-2 w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-100 transition-colors border-b border-gray-100 last:border-b-0 bg-white"
                                    >
                                      <Icon
                                        className={`h-4 w-4 ${colorClass}`}
                                      />
                                      <span>{label}</span>
                                    </button>
                                  ) : label === "Sửa" ? (
                                    <button
                                      key={label}
                                      onClick={() => {
                                        setOpenMenuArticleId(null);
                                        navigate(`/articles/edit/${item.id}`);
                                      }}
                                      className="flex items-center gap-2 w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-100 transition-colors border-b border-gray-100 last:border-b-0 bg-white"
                                    >
                                      <Icon
                                        className={`h-4 w-4 ${colorClass}`}
                                      />
                                      <span>{label}</span>
                                    </button>
                                  ) : label === "Xóa" ? (
                                    <button
                                      key={label}
                                      onClick={() => {
                                        setDeleteArticleId(item.id);
                                        setShowDeleteConfirm(true);
                                        setOpenMenuArticleId(null);
                                      }}
                                      className="flex items-center gap-2 w-full text-left px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition-colors border-b border-gray-100 last:border-b-0 bg-white"
                                    >
                                      <Icon
                                        className={`h-4 w-4 ${colorClass}`}
                                      />
                                      <span>{label}</span>
                                    </button>
                                  ) : null
                                )}
                              </div>
                            </div>,
                            document.body
                          )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination inside the table container */}
          <div className="flex justify-between items-center p-4 border-t border-gray-200">
            <div className="text-sm font-medium text-gray-700">
              Hiển thị <span className="font-bold">1</span> đến{" "}
              <select className="font-bold text-blue-600 border-none bg-transparent focus:ring-0 focus:outline-none text-sm">
                <option>10</option>
                <option>20</option>
                <option>50</option>
                <option>100</option>
              </select>{" "}
              trong <span className="font-bold">10000</span> kết quả
            </div>
            <div className="flex space-x-2">
              <button className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50">
                &lt;
              </button>
              <button className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50">
                1
              </button>
              <button className="px-3 py-2 text-sm font-medium text-white bg-[#337ab7] border border-[#337ab7] rounded-md">
                2
              </button>
              <button className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50">
                3
              </button>
              <button className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50">
                &gt;
              </button>
            </div>
          </div>
        </div>

        {/* Delete Confirmation Modal */}
        {showDeleteConfirm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[100000]">
            <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
              <div className="flex items-center mb-4">
                <div className="flex-shrink-0 w-10 h-10 mx-auto bg-red-100 rounded-full flex items-center justify-center">
                  <Trash2 className="h-6 w-6 text-red-600" />
                </div>
              </div>
              <div className="text-center">
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  Xác nhận xóa
                </h3>
                <p className="text-sm text-gray-500 mb-6">
                  Bạn có chắc chắn muốn xóa bài báo này không? Hành động này
                  không thể hoàn tác.
                </p>
                <div className="flex space-x-3 justify-center">
                  <button
                    onClick={handleDeleteCancel}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    Hủy
                  </button>
                  <button
                    onClick={handleDeleteConfirm}
                    className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                  >
                    Xóa
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </PageShell>
  );
};

export default ScientificArticles;
